/**
 * Profile Settings Component
 * Contains all profile settings: Password, Security, Language, Timezone, Date Format
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../components/shared/Toast';
import { useLanguage } from '../../context/LanguageContext';
import { useDateFormat } from '../../context/DateFormatContext';
import { authenticatedFetch } from '../../lib/api';
import {
  DATE_FORMAT_OPTIONS,
  formatWithDatePattern,
  formatWithTimePattern,
} from '../../lib/date-format';
import {
  MdLock,
  MdSecurity,
  MdTranslate,
  MdAccessTime,
  MdCalendarToday,
  MdKeyboardArrowDown,
  MdAccountBalance,
} from 'react-icons/md';
import { PaymentSettingsContent } from '../Settings/SettingsPage';
import { isPasswordStrong, PASSWORD_POLICY_MESSAGE } from '@euroasiann/shared';
import { PasswordRequirementsHint } from '@euroasiann/shared/ui';

interface ProfileSettingsProps {
  activeTab?: 'password' | 'security' | 'language' | 'timezone' | 'date-format' | 'banking' | null;
}

export function ProfileSettings({ activeTab = null }: ProfileSettingsProps) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [expanded, setExpanded] = useState<string | null>(activeTab);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    setExpanded(activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (!activeTab) return;
    const el = sectionRefs.current[activeTab];
    if (!el) return;
    // Wait a tick so the panel can expand before scrolling
    const t = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(t);
  }, [activeTab, expanded]);

  const handleTabChange = (
    tab: 'password' | 'security' | 'language' | 'timezone' | 'date-format' | 'banking'
  ) => {
    const next = expanded === tab ? null : tab;
    setExpanded(next);
    const newSearchParams = new URLSearchParams(searchParams);
    if (next) {
      newSearchParams.set('tab', next);
    } else {
      newSearchParams.delete('tab');
    }
    const qs = newSearchParams.toString();
    navigate(qs ? `/profile?${qs}` : '/profile', { replace: true });
  };

  const tabs = [
    { id: 'password' as const, label: t('Change/Reset Password'), icon: MdLock },
    { id: 'security' as const, label: t('Security Questions'), icon: MdSecurity },
    { id: 'language' as const, label: t('Language Selection'), icon: MdTranslate },
    { id: 'timezone' as const, label: t('Time Zone'), icon: MdAccessTime },
    { id: 'date-format' as const, label: t('Date and Time Format'), icon: MdCalendarToday },
    { id: 'banking' as const, label: t('Banking Details'), icon: MdAccountBalance },
  ];

  return (
    <div
      id="profile-account-settings"
      className="bg-[hsl(var(--card))] rounded-xl border border-[hsl(var(--border))] p-4 sm:p-5 scroll-mt-24"
    >
      <h2 className="text-lg font-bold text-[hsl(var(--foreground))] mb-4">Account settings</h2>
      <div className="space-y-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isOpen = expanded === tab.id;
          return (
            <div
              key={tab.id}
              id={`profile-setting-${tab.id}`}
              ref={(node) => {
                sectionRefs.current[tab.id] = node;
              }}
              className="rounded-lg border border-[hsl(var(--border))] overflow-hidden scroll-mt-28"
            >
              <button
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium bg-[hsl(var(--background))] hover:bg-[#0088cc]/5"
                aria-expanded={isOpen}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-[#0088cc]" />
                  <span>{tab.label}</span>
                </div>
                <MdKeyboardArrowDown
                  className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isOpen && (
                <div className="px-4 py-4 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                  {tab.id === 'password' && <PasswordSettings />}
                  {tab.id === 'security' && <SecurityQuestionsSettings />}
                  {tab.id === 'language' && <LanguageSettings />}
                  {tab.id === 'timezone' && <TimezoneSettings />}
                  {tab.id === 'date-format' && <DateFormatSettings />}
                  {tab.id === 'banking' && <PaymentSettingsContent />}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Password Settings Component
function PasswordSettings() {
  const { showToast } = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }

    if (!isPasswordStrong(newPassword)) {
      showToast(PASSWORD_POLICY_MESSAGE, 'error');
      return;
    }

    setIsSaving(true);
    try {
      const response = await authenticatedFetch('/api/v1/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to change password');
      }

      showToast('Password changed successfully', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      showToast(error.message || 'Failed to change password', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-2">Change Password</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Update your password to keep your account secure</p>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="currentPassword" className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
            Current Password
          </label>
          <input
            type="password"
            id="currentPassword"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full px-4 py-2.5 border border-[#0088cc]/25 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-colors text-[hsl(var(--foreground))] bg-white"
            placeholder="Enter current password"
          />
        </div>

        <div>
          <label htmlFor="newPassword" className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
            New Password
          </label>
          <input
            type="password"
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-4 py-2.5 border border-[#0088cc]/25 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-colors text-[hsl(var(--foreground))] bg-white"
            placeholder="Min 8 chars, uppercase, number, symbol"
          />
          <PasswordRequirementsHint password={newPassword} />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
            Confirm New Password
          </label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-4 py-2.5 border border-[#0088cc]/25 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-colors text-[hsl(var(--foreground))] bg-white"
            placeholder="Confirm new password"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={
            isSaving ||
            !currentPassword ||
            !isPasswordStrong(newPassword) ||
            newPassword !== confirmPassword
          }
          className="px-6 py-2.5 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold rounded-lg hover:bg-[#0077b3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Change Password'}
        </button>
      </div>
    </div>
  );
}

// Security Questions Settings Component
function SecurityQuestionsSettings() {
  const { showToast } = useToast();
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const securityQuestions = [
    'What was the name of your first pet?',
    'What city were you born in?',
    'What was your mother\'s maiden name?',
    'What was the name of your elementary school?',
    'What was your favorite food as a child?',
  ];

  const handleSave = async () => {
    if (!selectedQuestion || !answer.trim()) {
      showToast('Please select a security question and provide an answer', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const response = await authenticatedFetch('/api/v1/auth/security-question', {
        method: 'PUT',
        body: JSON.stringify({
          question: selectedQuestion,
          answer: answer.trim(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save security question');
      }

      showToast('Security question saved successfully', 'success');
      setSelectedQuestion('');
      setAnswer('');
    } catch (error: any) {
      showToast(error.message || 'Failed to save security question', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-2">Security Questions</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Set up a security question to help recover your account</p>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="securityQuestion" className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
            Select a Security Question
          </label>
          <select
            id="securityQuestion"
            value={selectedQuestion}
            onChange={(e) => setSelectedQuestion(e.target.value)}
            className="w-full px-4 py-2.5 border border-[#0088cc]/25 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-colors text-[hsl(var(--foreground))] bg-white appearance-none pr-10"
          >
            <option value="">Select a security question</option>
            {securityQuestions.map((question, index) => (
              <option key={index} value={question}>
                {question}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="securityAnswer" className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
            Your Answer
          </label>
          <input
            type="text"
            id="securityAnswer"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Enter your answer"
            className="w-full px-4 py-2.5 border border-[#0088cc]/25 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-colors text-[hsl(var(--foreground))] bg-white"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2.5 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold rounded-lg hover:bg-[#0077b3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Security Question'}
        </button>
      </div>
    </div>
  );
}

// Language Settings Component
function LanguageSettings() {
  const { showToast } = useToast();
  const { language, setLanguage, t } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [isSaving, setIsSaving] = useState(false);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'German (Deutsch)' },
    { code: 'fr', name: 'French (Français)' },
    { code: 'es', name: 'Spanish (Español)' },
    { code: 'it', name: 'Italian (Italiano)' },
    { code: 'pt', name: 'Portuguese (Português)' },
    { code: 'nl', name: 'Dutch (Nederlands)' },
    { code: 'pl', name: 'Polish (Polski)' },
    { code: 'ru', name: 'Russian (Русский)' },
  ];

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await authenticatedFetch('/api/v1/auth/preferences', {
        method: 'PUT',
        body: JSON.stringify({
          language: selectedLanguage,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save language preference');
      }

      setLanguage(selectedLanguage);
      showToast(t('Language preference saved successfully'), 'success');
    } catch (error: any) {
      showToast(error.message || 'Failed to save language preference', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-2">{t('Language Selection')}</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('Choose your preferred language for the interface')}</p>
      </div>

      <div>
        <label htmlFor="language" className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
          {t('Select Language')}
        </label>
        <select
          id="language"
          value={selectedLanguage}
          onChange={(e) => {
            const next = e.target.value;
            setSelectedLanguage(next);
            setLanguage(next);
          }}
          className="w-full px-4 py-2.5 border border-[#0088cc]/25 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-colors text-[hsl(var(--foreground))] bg-white"
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2.5 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold rounded-lg hover:bg-[#0077b3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? `${t('Saving')}...` : t('Save Language')}
        </button>
      </div>
    </div>
  );
}

// Timezone Settings Component
function TimezoneSettings() {
  const { showToast } = useToast();
  const [selectedTimezone, setSelectedTimezone] = useState('UTC');
  const [isSaving, setIsSaving] = useState(false);

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Rome',
    'Europe/Madrid',
    'Asia/Dubai',
    'Asia/Kolkata',
    'Asia/Tokyo',
    'Australia/Sydney',
  ];

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await authenticatedFetch('/api/v1/auth/preferences', {
        method: 'PUT',
        body: JSON.stringify({
          timezone: selectedTimezone,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save timezone');
      }

      showToast('Timezone saved successfully', 'success');
    } catch (error: any) {
      showToast(error.message || 'Failed to save timezone', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-2">Time Zone</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Set your timezone to display dates and times correctly</p>
      </div>

      <div>
        <label htmlFor="timezone" className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
          Select Time Zone
        </label>
        <select
          id="timezone"
          value={selectedTimezone}
          onChange={(e) => setSelectedTimezone(e.target.value)}
          className="w-full px-4 py-2.5 border border-[#0088cc]/25 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-colors text-[hsl(var(--foreground))] bg-white"
        >
          {timezones.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2.5 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold rounded-lg hover:bg-[#0077b3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Timezone'}
        </button>
      </div>
    </div>
  );
}

// Date Format Settings Component
function DateFormatSettings() {
  const { showToast } = useToast();
  const {
    dateFormat: savedDateFormat,
    timeFormat: savedTimeFormat,
    setDateTimeFormats,
    loading: prefsLoading,
  } = useDateFormat();
  const [dateFormat, setDateFormat] = useState(savedDateFormat);
  const [timeFormat, setTimeFormat] = useState(savedTimeFormat);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDateFormat(savedDateFormat);
    setTimeFormat(savedTimeFormat);
  }, [savedDateFormat, savedTimeFormat]);

  const previewNow = new Date();
  const previewDate = formatWithDatePattern(previewNow, dateFormat);
  const previewTime = formatWithTimePattern(previewNow, timeFormat);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await authenticatedFetch('/api/v1/auth/preferences', {
        method: 'PUT',
        body: JSON.stringify({
          dateFormat,
          timeFormat,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to save date and time format');
      }

      // Apply immediately so the UI and localStorage persist across refresh.
      setDateTimeFormats(dateFormat, timeFormat);
      showToast('Date and time format saved successfully', 'success');
    } catch (error: any) {
      showToast(error.message || 'Failed to save date and time format', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-2">Date and Time Format</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Customize how dates and times are displayed</p>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="dateFormat" className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
            Date Format
          </label>
          <select
            id="dateFormat"
            value={dateFormat}
            disabled={prefsLoading || isSaving}
            onChange={(e) => setDateFormat(e.target.value as typeof dateFormat)}
            className="w-full px-4 py-2.5 border border-[#0088cc]/25 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-colors text-[hsl(var(--foreground))] bg-[hsl(var(--card))]"
          >
            {DATE_FORMAT_OPTIONS.map((format) => (
              <option key={format} value={format}>
                {format}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="timeFormat" className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
            Time Format
          </label>
          <select
            id="timeFormat"
            value={timeFormat}
            disabled={prefsLoading || isSaving}
            onChange={(e) => setTimeFormat(e.target.value as typeof timeFormat)}
            className="w-full px-4 py-2.5 border border-[#0088cc]/25 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-colors text-[hsl(var(--foreground))] bg-[hsl(var(--card))]"
          >
            <option value="12h">12-hour (AM/PM)</option>
            <option value="24h">24-hour</option>
          </select>
        </div>

        <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            Preview
          </p>
          <p className="mt-1 text-sm font-medium text-[hsl(var(--foreground))]">
            {previewDate} · {previewTime} UTC
          </p>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || prefsLoading}
          className="px-6 py-2.5 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold rounded-lg hover:bg-[#0077b3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Format'}
        </button>
      </div>
    </div>
  );
}


